import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { FotoFilmShowcase } from "@/components/site/foto/FotoFilmShowcase";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, photosBy } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("kampanj", "kampanj");

const PATH = "/foto/kampanjfoto";
const TITLE = "Kampanjfoto och varumärkesbilder för företag | Fenny";
const DESCRIPTION =
  "Kampanjfoto med regisserat bildmanér för annons, sajt och sociala medier. Koncept, casting, produktion och retusch. Offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är skillnaden mellan kampanjfoto och vanlig företagsfotografering?",
    a: "Kampanjfoto är regisserat mot ett budskap: bilderna byggs utifrån ett koncept, ofta med casting, styling och plats vald för uttrycket. Företagsfotografering dokumenterar er verksamhet som den ser ut. Kampanjbilder kostar mer per bild men bär en hel kampanj.",
  },
  {
    q: "Behöver vi ett koncept innan vi bokar?",
    a: "Nej. Vi tar fram koncept och moodboard som del av uppdraget, precis som i våra filmproduktioner. Har ni redan en kampanjplattform utgår vi från den.",
  },
  {
    q: "Hur hanteras rättigheter för modeller och platser?",
    a: "Vi tecknar modellavtal med alla medverkande och begär in tillstånd för platser. Nyttjandeperiod och kanaler skrivs in i offerten så att ni vet exakt vad ni får använda och hur länge.",
  },
  {
    q: "Kan vi använda kampanjbilderna i annonser?",
    a: "Ja. Vi specificerar användningen redan i offerten — egna kanaler, köpt media, utomhus och tryck — så att inget behöver eftergodkännas när kampanjen väl rullar.",
  },
  {
    q: "Vad kostar kampanjfoto?",
    a: "Priset offereras per uppdrag. Kostnadsdrivarna är antal bilder, om vi arbetar med casting och styling, hur många platser som ingår och hur brett bilderna ska användas.",
  },
];

export const Route = createFileRoute("/foto/kampanjfoto")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Kampanjfoto — Fenny Foto" },
      { property: "og:description", content: "Regisserade varumärkesbilder för kampanj och annons." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vad är kampanjfoto och hur planeras en kampanjfotografering?",
            answer:
              "Kampanjfoto är bilder som produceras för en bestämd kampanj eller ett varumärkesuttryck: koncept, casting, plats, ljus och bildmanér bestäms i förväg och bilderna levereras i format för annons, sajt, sociala kanaler och tryck. Fenny planerar kampanjfotografering med moodboard och bildlista, fotograferar i studio eller på location och offererar per uppdrag utifrån omfattning och användningsrätt. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Kampanjfoto",
            path: PATH,
            serviceType: "Kampanjfotografering",
            description: "Kampanj- och varumärkesbilder med koncept, casting och regi.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Kampanjfoto — exempel",
            path: PATH,
            description: "Exempel på kampanj- och varumärkesbilder av Fenny för Fenny.",
            images: photosBy("kampanj"),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Kampanjfoto",
              "Varumärkesbilder",
              "Reklamfotografering",
              "Fotograf Stockholm",
              "Art direction",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Kampanjfoto", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const kampanj = photosBy("kampanj");
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Kampanjfoto"
      title={<>Kampanjbilder som bär ett budskap, inte bara en produkt.</>}
      lede="Regisserat kampanjfoto för annons, sajt och sociala medier. Vi tar fram koncept, castar, riggar ljus och levererar en bildserie som håller ihop över alla format — från helsidesannons till story."
      tldr={
        <p>
          Kampanjfoto omfattar <strong>koncept, casting, styling, fotografering
          och retusch</strong>. Rättigheter och nyttjandeperiod specificeras i
          offerten. Vi arbetar med <strong>offert per uppdrag</strong>, inte
          fasta paketpriser.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Se portfolion", to: "/foto" }}
      trackingId="foto_kampanjfoto"
      stills={false}
      videos={false}
      afterHero={
        <div className="py-12">
          <PhotoMasonry
            photos={kampanj}
            filterable={false}
            initial={12}
            eyebrow="Kampanj"
            heading="Bildserier ur skarpa uppdrag"
          />
        </div>
      }
      bullets={[
        { title: "Koncept före kamera", body: "Moodboard, bildlista och format bestäms innan vi bokar dag — inga gissningar på plats." },
        { title: "Ett manér, alla format", body: "Vi fotograferar med beskärning för både liggande annons och stående social." },
        { title: "Rättigheter i klartext", body: "Modellavtal, kanaler och nyttjandeperiod står i offerten." },
      ]}
      process={{
        heading: "Från brief till bildbank",
        steps: [
          { label: "Brief", body: "Målgrupp, budskap och var bilderna ska synas.", duration: "vecka 1" },
          { label: "Koncept", body: "Moodboard, bildlista och plats- eller castingförslag.", duration: "vecka 1–2" },
          { label: "Förproduktion", body: "Casting, styling, tillstånd och schema.", duration: "vecka 2–3" },
          { label: "Fotografering", body: "Halv- eller heldag med regi och ljusteam.", duration: "1 dag" },
          { label: "Efterarbete", body: "Urval, retusch, färgsättning och leverans per format.", duration: "3–7 dagar" },
        ],
      }}
      sections={[
        {
          id: "kampanj-och-film",
          heading: "Kampanjfoto och reklamfilm i samma produktion",
          body: (
            <p>
              Ska kampanjen även innehålla rörligt? Boka fotograferingen samma dag
              som{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">
                reklamfilmen
              </a>{" "}
              — ljus, plats, casting och styling är då redan betalt en gång.
              Kombinationen sänker normalt totalkostnaden märkbart jämfört med två
              separata produktionsdagar. Se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foto-och-film">
                foto och film samtidigt
              </a>
              .
            </p>
          ),
        },
        {
          id: "vad-paverkar-priset",
          heading: "Vad som påverkar priset",
          body: (
            <p>
              Antal bilder, antal platser, casting och styling, samt hur brett
              bilderna ska användas. En bildserie som bara ska ligga på sajten är
              betydligt billigare än samma serie med rikstäckande utomhusannonsering
              i två år. Vi går igenom hela kalkylen i{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/vad-kostar-en-fotograf">
                vad kostar en fotograf
              </a>
              .
            </p>
          ),
        },
      ]}
      afterSections={<FotoFilmShowcase offset={2} heading="Kampanjen i två format" lede="Samma kampanj levererad som rörligt för annonsering och stillbilder för sajt, tryck och sociala kanaler." />}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Har ni en kampanj på gång?",
        body: "Skicka briefen så återkommer vi med konceptförslag och fast pris.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_kampanjfoto" defaultAssignment="kampanjfoto" />}
    />
  );
}
